import { ConvexHull } from '@data/buildingProjects/DesignSchemes/SingleBuilding/ConvexHull';
import { alphaShapeHullLoops, simplifyCollinear } from '@data/geometry/ConcaveHull';
import FVector from '@data/geometry/FVector';
import { GeometryUtils } from '@data/geometry/GeometryUtils';
import { ConvexHullDesignState, LayerKind } from '@visualizer/types';
import { getViewer } from 'store/viewerStore';
import * as THREE from 'three';
import { ConvexGeometry } from 'three/addons/geometries/ConvexGeometry.js';
import { mergeGeometries, mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import Grid from '../../../Geodata/grid';
import { DesignSchemeRenderer } from '../DesignSchemeRenderer';
import { generatePointsCloud, removeDuplicatePoints } from '../DesignSchemeUtils';
import { PolygonShapeBuilder } from '../PolygonShapeBuilder';
import { tagDesignSchemeSurfaceMaterial } from '../DesignSchemeSurfaceMaterialHelper';

export class ConvexHullRenderer extends DesignSchemeRenderer {
    private convexHull: ConvexHull;
    declare protected config: ConvexHullDesignState;

    constructor(convexHullScheme: ConvexHull, config: ConvexHullDesignState) {
        super(config);
        this.convexHull = convexHullScheme;
    }

    public updateConfig(newConfig: Partial<ConvexHullDesignState>) {
        this.config = this.cloneConfig({ ...this.config, ...newConfig } as ConvexHullDesignState);
    }

    // =====================================
    // CONCAVE STROKES
    // =====================================

    // Normalizes z values to a stable key so adjacent levels can be grouped reliably.
    private levelKey(z: number): string {
        return z.toFixed(6);
    }

    // Merges overlapping parameter intervals on the same edge.
    private mergeIntervals(intervals: Array<[number, number]>, eps: number): Array<[number, number]> {
        if (intervals.length === 0) return [];
        intervals.sort((u, v) => u[0] - v[0]);
        const merged: Array<[number, number]> = [intervals[0]];
        for (let i = 1; i < intervals.length; i++) {
            const prev = merged[merged.length - 1];
            const curr = intervals[i];
            if (curr[0] <= prev[1] + eps) {
                prev[1] = Math.max(prev[1], curr[1]);
            } else {
                merged.push(curr);
            }
        }
        return merged;
    }

    // Computes uncovered edge ranges after subtracting segments already present on a neighbor z-level.
    private subtractCoverageByLevel(
        a: THREE.Vector2,
        b: THREE.Vector2,
        levelLoops: THREE.Vector2[][],
        tol: number
    ): Array<[number, number]> {
        const d = new THREE.Vector2(b.x - a.x, b.y - a.y);
        const dLen = d.length();
        if (dLen <= 1e-12) return [];

        const covered: Array<[number, number]> = [];
        for (const loop of levelLoops) {
            for (let i = 0; i < loop.length; i++) {
                const q0 = loop[i];
                const q1 = loop[(i + 1) % loop.length];
                const e = new THREE.Vector2(q1.x - q0.x, q1.y - q0.y);
                const eLen = e.length();
                if (eLen <= 1e-12) continue;

                // Same line condition: parallel and both endpoints close to line AB:
                const parallelCross = Math.abs(d.x * e.y - d.y * e.x);
                if (parallelCross > tol * Math.max(dLen, eLen)) continue;
                if (GeometryUtils.distanceToInfiniteLine2D(q0, a, b) > tol || GeometryUtils.distanceToInfiniteLine2D(q1, a, b) > tol) {
                    continue;
                }

                const t0 = GeometryUtils.projectPointToLineParam2D(q0, a, b);
                const t1 = GeometryUtils.projectPointToLineParam2D(q1, a, b);
                const lo = Math.max(0, Math.min(t0, t1));
                const hi = Math.min(1, Math.max(t0, t1));
                if (hi - lo <= 1e-9) continue;
                covered.push([lo, hi]);
            }
        }

        const mergedCovered = this.mergeIntervals(covered, 1e-6);
        if (mergedCovered.length === 0) return [[0, 1]];

        const uncovered: Array<[number, number]> = [];
        let cursor = 0;
        for (const [lo, hi] of mergedCovered) {
            if (lo > cursor + 1e-6) uncovered.push([cursor, lo]);
            cursor = Math.max(cursor, hi);
        }
        if (cursor < 1 - 1e-6) uncovered.push([cursor, 1]);

        const minParamLen = Math.max(1e-6, (Grid.gridRes * 0.03) / dLen);
        return uncovered.filter(([u0, u1]) => u1 - u0 > minParamLen);
    }

    private generateSegmentPart(segments: number[], a: THREE.Vector2, b: THREE.Vector2, z: number, t0: number, t1: number) {
        const x0 = a.x + (b.x - a.x) * t0;
        const y0 = a.y + (b.y - a.y) * t0;
        const x1 = a.x + (b.x - a.x) * t1;
        const y1 = a.y + (b.y - a.y) * t1;
        segments.push(x0, y0, z, x1, y1, z);
    }

    // Builds strokes for 2.5D Concave Hulls (to avoid seeing strokes at every floor):
    private buildContourEdges(contours: Array<{ zTop: number; zBottom: number; loop: THREE.Vector2[] }>): THREE.BufferGeometry {
        const byTopZ = new Map<string, THREE.Vector2[][]>();
        for (const c of contours) {
            const key = this.levelKey(c.zTop);
            const loops = byTopZ.get(key) ?? [];
            loops.push(c.loop);
            byTopZ.set(key, loops);
        }

        const segments: number[] = [];
        for (const contour of contours) {
            const { zTop, zBottom, loop } = contour;
            if (loop.length < 3) continue;
            const aboveLoops = byTopZ.get(this.levelKey(zTop + Grid.gridRes)) ?? [];
            const belowLoops = byTopZ.get(this.levelKey(zTop - Grid.gridRes)) ?? [];
            const tol = Grid.gridRes * 0.02;
            for (let i = 0; i < loop.length; i++) {
                const a = loop[i];
                const b = loop[(i + 1) % loop.length];
                const topParts = this.subtractCoverageByLevel(a, b, aboveLoops, tol);
                for (const [t0, t1] of topParts) this.generateSegmentPart(segments, a, b, zTop, t0, t1);

                const bottomParts = this.subtractCoverageByLevel(a, b, belowLoops, tol);
                for (const [t0, t1] of bottomParts) this.generateSegmentPart(segments, a, b, zBottom, t0, t1);
            }
        }
        const geom = new THREE.BufferGeometry();
        geom.setAttribute('position', new THREE.Float32BufferAttribute(segments, 3));
        return geom;
    }

    // Removes tiny and optional intermediate edges to keep wireframes readable.
    private removeIntermediateHorizontalEdges(
        geometry: THREE.BufferGeometry,
        edges: THREE.BufferGeometry,
        keepHorizontalEdges: boolean,
        concaveCleanup: boolean
    ): THREE.BufferGeometry {
        geometry.computeBoundingBox();
        const bounds = geometry.boundingBox;
        if (!bounds) return edges;

        const pos = edges.getAttribute('position');
        if (!pos) return edges;
        const src = pos.array as Float32Array;

        const kept: number[] = [];
        for (let i = 0; i < src.length; i += 6) {
            const x1 = src[i];
            const y1 = src[i + 1];
            const z1 = src[i + 2];
            const x2 = src[i + 3];
            const y2 = src[i + 4];
            const z2 = src[i + 5];
            const isHorizontal = Math.abs(z1 - z2) <= 1e-6;
            if (!keepHorizontalEdges && isHorizontal) continue;
            const dx = x2 - x1;
            const dy = y2 - y1;
            const dz = z2 - z1;
            const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
            const horizLen = Math.sqrt(dx * dx + dy * dy);
            const isNearVertical = horizLen <= Grid.gridRes * 0.01;

            // Drop tiny artifacts, mostly coming from roof triangulation.
            if (len < Grid.gridRes * 0.12) continue;
            if (isNearVertical && len < Grid.gridRes * 0.75) continue;
            if (concaveCleanup && isNearVertical && len <= Grid.gridRes * 0.35) continue;

            kept.push(x1, y1, z1, x2, y2, z2);
        }

        const filtered = new THREE.BufferGeometry();
        filtered.setAttribute('position', new THREE.Float32BufferAttribute(kept, 3));
        return filtered;
    }

    // =====================================
    // HULL MESH GENERATION
    // =====================================

    // Generates the final solid mesh and edge (storkes) for convex or 2.5D concave hull:
    async generateMesh() {
        // 1. Get sensors & config (state) params:
        const sensors = this.convexHull.getSensorDatasFor(LayerKind.MAIN);

        let pointsCloud = generatePointsCloud(sensors);
        pointsCloud = removeDuplicatePoints(pointsCloud);
        const useConcave = this.config.concave ?? false;
        const transparent = this.config.transparent ?? true;
        const concaveAlpha = this.config.concaveAlpha?.value ?? 10.0;

        let hullGeometry: THREE.BufferGeometry;
        const concaveContours: Array<{ zTop: number; zBottom: number; loop: THREE.Vector2[] }> = [];

        if (useConcave) {
            // 2. Generate 2.5D Concave Hull: every z is an extruded shape (optionally blending nearby layers):
            const geometries: THREE.BufferGeometry[] = [];
            const halfSide = Grid.gridRes / 2;
            const activeCenters = sensors.filter((sensor) => sensor && sensor.getScalarValue() !== 0).map((sensor) => sensor.getPosition());

            // 2.1 Compute z-levels:
            const zLevels = Array.from(new Set(activeCenters.map((p) => Number(p.z.toFixed(6))))).sort((a, b) => a - b);
            if (zLevels.length === 0) return;

            // 2.2. Make alpha affect also z-levels:
            const aMin = this.config.concaveAlpha?.minValue ?? 0.8;
            const aMax = this.config.concaveAlpha?.maxValue ?? 10.0;
            const aNorm = THREE.MathUtils.clamp((concaveAlpha - aMin) / Math.max(1e-6, aMax - aMin), 0, 1);
            const neighborSpan = Math.round(aNorm * 2); // 0..2 nearby levels influenced by alpha

            // 2.3. Loop on levels and compute 2D hull (for every floor):
            for (let i = 0; i < zLevels.length; i++) {
                if (i > 0 && i % 5 === 0) {
                    await this.yieldToUi();
                    if (this.shouldAbortGeneration()) return;
                }

                // 2.3.1. Get the centers at current Level and/or neighbouring levels if Concaveness is big enough:
                const centerZ = zLevels[i];
                const z0 = centerZ - halfSide;
                const depth = Grid.gridRes;
                const startIdx = Math.max(0, i - neighborSpan);
                const endIdx = Math.min(zLevels.length - 1, i + neighborSpan);
                const consideredZ = new Set<number>(zLevels.slice(startIdx, endIdx + 1));
                const centersAtLevel = activeCenters.filter((p) => Array.from(consideredZ).some((z) => Math.abs(p.z - z) <= 1e-6));

                // 2.3.2 Get corner points of sensors for hull:
                const uniqueXYMap = new Map<string, THREE.Vector2>();
                for (const p of centersAtLevel) {
                    const corners = [
                        new THREE.Vector2(p.x - halfSide, p.y - halfSide),
                        new THREE.Vector2(p.x - halfSide, p.y + halfSide),
                        new THREE.Vector2(p.x + halfSide, p.y - halfSide),
                        new THREE.Vector2(p.x + halfSide, p.y + halfSide),
                    ];
                    for (const c of corners) {
                        const key = `${c.x.toFixed(6)},${c.y.toFixed(6)}`;
                        if (!uniqueXYMap.has(key)) uniqueXYMap.set(key, c);
                    }
                }

                // 2.3.3. Build 2D concave hull loops per level (supports disconnected clusters):
                const uniqueLevelPoints = Array.from(uniqueXYMap.values()).map((v) => new FVector(v.x, v.y, centerZ));
                const loops = alphaShapeHullLoops(uniqueLevelPoints, concaveAlpha)
                    .map((loop) => simplifyCollinear(loop))
                    .filter((loop) => loop.length >= 3);

                // 2.3.4. Handle disconnected clusters/loops (e.g. two towers):
                const shapes = PolygonShapeBuilder.createShapesFromLoops(loops);
                for (const loop of loops) {
                    concaveContours.push({
                        zTop: z0 + depth,
                        zBottom: z0,
                        loop: loop.map((p) => new THREE.Vector2(p.x, p.y)),
                    });
                }

                for (const shape of shapes) {
                    const geom = new THREE.ExtrudeGeometry(shape, {
                        depth,
                        bevelEnabled: false,
                        steps: 1,
                    });
                    geom.translate(0, 0, z0);
                    geometries.push(geom);
                }
            }

            if (geometries.length === 0) return;

            // 2.4. Merge slabs into one smooth mesh and recompute normals:
            hullGeometry = mergeGeometries(geometries, false) ?? geometries[0];
            hullGeometry = mergeVertices(hullGeometry, 1e-5);
            hullGeometry.computeVertexNormals();
        } else {
            // 3. Convex mode: create a classic 3D convex hull from all points:
            hullGeometry = new ConvexGeometry(pointsCloud.map((f) => new THREE.Vector3(f.x, f.y, f.z)));
        }

        // 4. Create and add the filled hull mesh:
        const material = new THREE.MeshBasicMaterial({
            color: 0x808080,
            wireframe: false,
            side: THREE.DoubleSide,
            transparent,
            opacity: transparent ? 0.5 : 1,
            polygonOffset: true,
            polygonOffsetFactor: 1,
            polygonOffsetUnits: 1,
        });
        tagDesignSchemeSurfaceMaterial(material, 'opaque');
        const mesh = new THREE.Mesh(hullGeometry, material);

        // 5. Build Strokes for concave mode:
        const weldedGeometry = mergeVertices(hullGeometry.clone(), 1e-5);
        weldedGeometry.computeVertexNormals();
        const rawEdges = new THREE.EdgesGeometry(weldedGeometry, useConcave ? 1 : 1);
        const edges = this.removeIntermediateHorizontalEdges(weldedGeometry, rawEdges, !useConcave, useConcave);
        const edgeMaterial = new THREE.LineBasicMaterial({
            color: 0x000000,
            polygonOffset: true,
            depthTest: true,
            polygonOffsetFactor: 3,
            polygonOffsetUnits: 1,
            linewidth: 1,
        });
        const lineMesh = new THREE.LineSegments(edges, edgeMaterial);
        const contourMesh = useConcave ? new THREE.LineSegments(this.buildContourEdges(concaveContours), edgeMaterial) : undefined;

        // 6. Done:
        this.meshGroup.add(mesh);
        this.meshGroup.add(lineMesh);
        if (contourMesh) this.meshGroup.add(contourMesh);

        const scene: THREE.Scene = getViewer().getScene();
        scene.add(this.meshGroup);
    }
}
